import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, ShieldCheck } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Shell({ children }) {
  return (
    <div className="grid min-h-screen place-items-center bg-[#0B1018] px-4">
      <div className="w-full max-w-md space-y-5 rounded-xl border border-[#C2A462]/40 bg-[#111722] p-8 shadow-2xl fade-up" data-testid="secure-doc-page">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#C2A462]"><Lock className="h-3.5 w-3.5" />Confidential document</div>
        {children}
      </div>
    </div>
  );
}

function SmsStep({ token, info, open }) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const send = async () => { setBusy(true); setErr(""); try { await api.post(`/public/secure-docs/${token}/send-code`); setSent(true); } catch (e) { setErr(errMsg(e)); } setBusy(false); };
  const verify = async () => { setBusy(true); setErr(""); try { open((await api.post(`/public/secure-docs/${token}/verify`, { code })).data.ticket); } catch (e) { setErr(errMsg(e)); } setBusy(false); };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-300">To protect your privacy, we'll text a one-time code to your mobile ending <b className="text-[#EBD6A0]">{info.phone_hint}</b>.</p>
      {!sent ? <Button className="w-full" onClick={send} disabled={busy} data-testid="secure-doc-send-code">{busy ? "Sending…" : "Text me a code"}</Button> : (
        <div className="space-y-2">
          <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className="text-center text-lg tracking-[0.4em]" data-testid="secure-doc-code" />
          <Button className="w-full" onClick={verify} disabled={busy || code.length !== 6} data-testid="secure-doc-verify">Verify and open</Button>
          <button className="text-xs text-[#C2A462]" onClick={send} data-testid="secure-doc-resend">Send a new code</button>
        </div>)}
      {err && <p className="text-xs text-red-300" data-testid="secure-doc-error">{err}</p>}
    </div>
  );
}

function SigninStep({ token, open }) {
  const [err, setErr] = useState("");
  const signedIn = !!localStorage.getItem("bos_token");
  const go = async () => { setErr(""); sessionStorage.setItem("bos_after_login", `/secure-doc/${token}`); try { open((await api.post(`/secure-docs/${token}/ticket`)).data.ticket); sessionStorage.removeItem("bos_after_login"); } catch (e) { setErr(errMsg(e)); } };
  const login = () => { sessionStorage.setItem("bos_after_login", `/secure-doc/${token}`); window.location.assign("/login"); };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-300">This document was sent to your BOS HQ account. Please sign in to open it.</p>
      {signedIn ? <Button className="w-full" onClick={go} data-testid="secure-doc-open-signed-in">Open document</Button> : <Button className="w-full" onClick={login} data-testid="secure-doc-sign-in">Sign in to BOS HQ</Button>}
      {err && <p className="text-xs text-red-300" data-testid="secure-doc-error">{err}</p>}
    </div>
  );
}

export default function SecureDoc() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => { api.get(`/public/secure-docs/${token}/info`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  const open = (ticket) => { window.location.assign(`${BACKEND}/api/public/secure-docs/${token}/download?ticket=${encodeURIComponent(ticket)}`); setDone(true); };
  if (err) return <Shell><p className="text-sm text-slate-300" data-testid="secure-doc-unavailable">{err}</p></Shell>;
  if (!info) return <Shell><p className="text-sm text-slate-400">Checking your link…</p></Shell>;
  return (
    <Shell>
      <div><div className="font-display text-2xl font-semibold text-white" data-testid="secure-doc-filename">{info.filename}</div><div className="text-xs text-slate-400">Sent securely by {info.sender}</div></div>
      {done ? <p className="flex items-center gap-2 text-sm text-emerald-300" data-testid="secure-doc-done"><ShieldCheck className="h-4 w-4" />Verified. Your download has started.</p>
        : info.method === "sms" ? <SmsStep token={token} info={info} open={open} />
          : info.method === "signin" ? <SigninStep token={token} open={open} />
            : <p className="text-sm text-slate-300" data-testid="secure-doc-no-phone">We need a mobile number to confirm who you are. Please contact {info.sender} and ask them to add your mobile to your record, then use this link again.</p>}
      <p className="text-[11px] text-slate-500">Every opening of this document is recorded. The sender can withdraw access at any time.</p>
    </Shell>
  );
}
